
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { RefObject } from 'react';

interface ValueItem {
  icon: string;
  title: string;
  description: string;
}

const values: ValueItem[] = [
  {
    icon: 'ri-lightbulb-line',
    title: 'Creativity First',
    description:
      'We don’t do cookie‑cutter. Every piece of content is crafted from scratch to reflect your unique brand voice and resonate with your specific audience.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Authentic Connection',
    description:
      'Real engagement beats vanity metrics. We build strategies that foster genuine relationships between your brand and the people who matter most.',
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Data‑Driven Results',
    description:
      'Beautiful content is only half the story. We obsess over analytics to ensure every post, reel, and campaign is driving real, measurable business growth.',
  },
  {
    icon: 'ri-team-line',
    title: 'True Partnership',
    description:
      'We’re not a faceless agency. We embed ourselves in your brand, becoming an extension of your team so your social presence feels seamlessly you.',
  },
];

export default function Values() {
  // useScrollAnimation returns a ref that we spread onto the root element.
  // Adding a generic type ensures the ref is correctly typed.
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();

  // Defensive guard – in case the hook ever returns an undefined ref.
  const sectionRef = (ref as RefObject<HTMLElement>) ?? { current: null };

  return (
    <section ref={sectionRef} className="py-28 bg-[#f8fafa]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div
          className={`
            text-center max-w-2xl mx-auto mb-16
            transition-all duration-700
            ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
          `}
        >
          <span className="text-xs font-semibold text-[#00989e] uppercase tracking-widest">
            What Drives Us
          </span>
          <h2
            className="text-3xl lg:text-4xl font-bold text-slate-900 mt-3 mb-4"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Our core values
          </h2>
          <p className="text-slate-600 text-[15px] leading-relaxed">
            These aren’t just words on a wall. They shape every strategy we build,
            every piece of content we create, and every relationship we nurture.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {values.map((value, index) => (
            <div
              key={`${value.title}-${index}`} // ensure a unique key
              className={`
                bg-white rounded-2xl p-7 shadow-sm hover:shadow-lg
                transition-all duration-500 group cursor-default
                ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
              `}
              style={{
                // Add a small staggered delay when the component becomes visible.
                transitionDelay: isVisible ? `${200 + index * 120}ms` : '0ms',
              }}
            >
              <div className="w-12 h-12 rounded-xl bg-[#00989e]/10 flex items-center justify-center mb-5 group-hover:bg-[#00989e] transition-colors duration-300">
                <i
                  className={`
                    ${value.icon}
                    text-xl text-[#00989e] group-hover:text-white
                    transition-colors duration-300
                  `}
                />
              </div>
              <h3
                className="text-lg font-bold text-slate-900 mb-2"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {value.title}
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed">{value.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
